import { useQuery } from '@tanstack/react-query';

import { callGet, callPost } from '@/api/fetcher';
import {
  Datasource,
  DatasourceType,
  GoogleDriveConfig,
  GoogleDrivePickedFile,
} from '@/shared/types/collections';

const CONFIG_ENDPOINT = '/www/api/integrations/google-drive/config';
const TOKEN_ENDPOINT = '/www/api/integrations/google-drive/token';

const GIS_SCRIPT = 'https://accounts.google.com/gsi/client';
const GAPI_SCRIPT = 'https://apis.google.com/js/api.js';
const DRIVE_FILES_URL = 'https://www.googleapis.com/drive/v3/files';

export const GOOGLE_DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
const FOLDER_MIME_TYPE = 'application/vnd.google-apps.folder';
const PICKER_LOAD_TIMEOUT_MS = 10_000;

export const SUPPORTED_MIME_TYPES = [
  'application/vnd.google-apps.document',
  'application/vnd.google-apps.spreadsheet',
  'application/vnd.google-apps.presentation',
  'text/plain',
  'text/markdown',
  'text/html',
  'text/csv',
  'text/tab-separated-values',
  'text/xml',
  'application/xml',
  'application/json',
  'application/yaml',
  'application/x-yaml',
  'text/yaml',
  'text/x-yaml',
];

const PICKER_MIME_TYPES = [FOLDER_MIME_TYPE, ...SUPPORTED_MIME_TYPES].join(',');

type GoogleDriveSettings =
  { enabled: false } | { enabled: true; client_id: string; api_key: string; app_id: string };

type EnabledSettings = Extract<GoogleDriveSettings, { enabled: true }>;

export type GoogleDriveTokens = { access_token: string; refresh_token: string };

export type GoogleDriveFormValues = {
  name?: string;
  refresh_token?: string;
  files?: GoogleDrivePickedFile[];
};

type CodeResponse = { code?: string; error?: string };

type DocsView = {
  setIncludeFolders: (included: boolean) => DocsView;
  setSelectFolderEnabled: (enabled: boolean) => DocsView;
  setMimeTypes: (mimeTypes: string) => DocsView;
  setEnableDrives: (enabled: boolean) => DocsView;
};

type PickerBuilder = {
  addView: (view: DocsView) => PickerBuilder;
  enableFeature: (feature: string) => PickerBuilder;
  setOAuthToken: (token: string) => PickerBuilder;
  setDeveloperKey: (key: string) => PickerBuilder;
  setAppId: (appId: string) => PickerBuilder;
  setOrigin: (origin: string) => PickerBuilder;
  setCallback: (callback: (data: PickerResponse) => void) => PickerBuilder;
  build: () => { setVisible: (visible: boolean) => void };
};

type PickerResponse = { action: string; docs?: { id: string; name: string }[] };

type GoogleApi = {
  accounts: {
    oauth2: {
      initCodeClient: (config: {
        client_id: string;
        scope: string;
        ux_mode: 'popup';
        callback: (response: CodeResponse) => void;
        error_callback: (error: { type: string }) => void;
      }) => { requestCode: () => void };
    };
  };
  picker: {
    DocsView: new (viewId: string) => DocsView;
    PickerBuilder: new () => PickerBuilder;
    ViewId: { DOCS: string };
    Feature: { SUPPORT_DRIVES: string; MULTISELECT_ENABLED: string };
    Action: { PICKED: string; CANCEL: string; ERROR: string };
  };
};

type GoogleWindow = Window & {
  google: GoogleApi;
  gapi: {
    load: (
      api: string,
      config: { callback: () => void; onerror: () => void; timeout: number; ontimeout: () => void }
    ) => void;
  };
};

const googleWindow = () => window as unknown as GoogleWindow;

const scripts = new Map<string, Promise<void>>();

const loadScript = (src: string) => {
  const cached = scripts.get(src);
  if (cached) {
    return cached;
  }
  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scripts.delete(src);
      script.remove();
      reject(new Error(`Failed to load ${src}`));
    };
    document.head.appendChild(script);
  });
  scripts.set(src, promise);
  return promise;
};

let pickerApi: Promise<void> | undefined;

export const loadPicker = () => {
  if (!pickerApi) {
    pickerApi = loadScript(GAPI_SCRIPT)
      .then(
        () =>
          new Promise<void>((resolve, reject) => {
            googleWindow().gapi.load('picker', {
              callback: resolve,
              onerror: () => reject(new Error('Failed to load the Google Picker')),
              timeout: PICKER_LOAD_TIMEOUT_MS,
              ontimeout: () => reject(new Error('Timed out loading the Google Picker')),
            });
          })
      )
      .catch((error: unknown) => {
        pickerApi = undefined;
        throw error;
      });
  }
  return pickerApi;
};

// Loaded before the click: a popup opened after a network wait is blocked by the browser.
export const useSignInScript = (enabled: boolean) =>
  useQuery({
    queryKey: [GIS_SCRIPT],
    queryFn: () => loadScript(GIS_SCRIPT).then(() => true),
    enabled,
    staleTime: Infinity,
    gcTime: Infinity,
  });

export const useGoogleDriveSettings = () =>
  useQuery({
    queryKey: [CONFIG_ENDPOINT],
    queryFn: (): Promise<GoogleDriveSettings> => callGet(CONFIG_ENDPOINT),
    staleTime: Infinity,
    gcTime: Infinity,
  });

/** Resolves `null` when the user closes the consent popup. */
export const requestAuthorizationCode = (clientId: string): Promise<string | null> =>
  new Promise((resolve, reject) => {
    // GIS adds access_type=offline and prompt=consent itself, so every code yields a refresh token.
    const client = googleWindow().google.accounts.oauth2.initCodeClient({
      client_id: clientId,
      scope: GOOGLE_DRIVE_SCOPE,
      ux_mode: 'popup',
      callback: (response) => {
        if (response.code) {
          resolve(response.code);
          return;
        }
        reject(new Error(response.error ?? 'missing_code'));
      },
      error_callback: (error) => {
        if (error.type === 'popup_closed') {
          resolve(null);
          return;
        }
        reject(new Error(error.type));
      },
    });
    client.requestCode();
  });

export const exchangeAuthorizationCode = (code: string): Promise<GoogleDriveTokens> =>
  callPost(TOKEN_ENDPOINT, { code });

const LOST_ACCESS_REASONS = new Set(['forbidden', 'insufficientFilePermissions', 'notFound']);

const canReadFile = async (fileId: string, accessToken: string) => {
  const params = new URLSearchParams({ fields: 'id,name', supportsAllDrives: 'true' });
  const response = await fetch(`${DRIVE_FILES_URL}/${encodeURIComponent(fileId)}?${params}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (response.ok) {
    return true;
  }
  if (response.status === 404) {
    return false;
  }
  if (response.status === 403) {
    const body = await response.json().catch(() => undefined);
    const reason = body?.error?.errors?.[0]?.reason;
    if (reason && LOST_ACCESS_REASONS.has(reason)) {
      return false;
    }
  }
  throw new Error(`Drive returned ${response.status}`);
};

/** The stored documents the account can still read, in the order they were stored. */
export const readableFiles = async (files: GoogleDrivePickedFile[], accessToken: string) => {
  const readable = await Promise.all(files.map((file) => canReadFile(file.id, accessToken)));
  return files.filter((_, index) => readable[index]);
};

const getTopOrigin = () => {
  const ancestors = window.location.ancestorOrigins;
  return ancestors?.length ? ancestors[ancestors.length - 1] : undefined;
};

/** Opens the Picker on documents. Resolves `null` on cancel and rejects on a Picker error. */
export const pickFiles = async (
  settings: EnabledSettings,
  accessToken: string
): Promise<GoogleDrivePickedFile[] | null> => {
  await loadPicker();
  const { picker } = googleWindow().google;

  const documentView = (sharedDrives: boolean) =>
    new picker.DocsView(picker.ViewId.DOCS)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(false)
      .setMimeTypes(PICKER_MIME_TYPES)
      .setEnableDrives(sharedDrives);

  return new Promise((resolve, reject) => {
    const builder = new picker.PickerBuilder()
      .addView(documentView(false))
      .addView(documentView(true))
      .enableFeature(picker.Feature.SUPPORT_DRIVES)
      .enableFeature(picker.Feature.MULTISELECT_ENABLED)
      .setOAuthToken(accessToken)
      .setDeveloperKey(settings.api_key)
      .setAppId(settings.app_id)
      .setCallback((data) => {
        if (data.action === picker.Action.PICKED) {
          resolve(data.docs?.length ? data.docs.map(({ id, name }) => ({ id, name })) : null);
        } else if (data.action === picker.Action.CANCEL) {
          resolve(null);
        } else if (data.action === picker.Action.ERROR) {
          reject(new Error('picker_error'));
        }
      });
    const topOrigin = getTopOrigin();
    if (topOrigin) {
      builder.setOrigin(topOrigin);
    }
    builder.build().setVisible(true);
  });
};

export const mergeFiles = (
  current: GoogleDrivePickedFile[] = [],
  added: GoogleDrivePickedFile[]
) => {
  const seen = new Set(current.map((file) => file.id));
  return [...current, ...added.filter((file) => !seen.has(file.id))];
};

/** An edit keeps the stored refresh token, so only a new source needs one. */
export const canSaveGoogleDrive = (
  { name, refresh_token, files }: GoogleDriveFormValues,
  isEditing: boolean
) => !!name?.trim() && !!files?.length && (isEditing || !!refresh_token);

export const buildGoogleDriveDatasource = (
  values: GoogleDriveFormValues
): Pick<Datasource, 'type' | 'name'> & { config: GoogleDriveConfig } => ({
  type: DatasourceType.GDRIVE,
  name: values.name.trim(),
  config: {
    files: values.files,
    ...(values.refresh_token && { refresh_token: values.refresh_token }),
  },
});

export const isGoogleDriveConfig = (config: unknown): config is GoogleDriveConfig =>
  typeof config === 'object' &&
  config !== null &&
  'files' in config &&
  Array.isArray((config as GoogleDriveConfig).files);
